import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, X, Loader2, ImageIcon, ScanSearch } from "lucide-react";

/**
 * ImageSearchPalette — futuristic dark palette to search Openverse
 * (via /api/images/search) and pick an image to insert into the whiteboard.
 *
 * Props:
 *   open       — boolean
 *   onClose    — () => void
 *   onPick     — (imageResult) => void   (result has `url`, `title`, `width`, `height`)
 *   defaultQ   — optional seed query (auto-searches when provided)
 */
export default function ImageSearchPalette({ open, onClose, onPick, defaultQ = "" }) {
  const [q, setQ] = useState(defaultQ);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);

  const runSearch = async (query) => {
    if (!query.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/images/search`, { params: { q: query.trim(), page_size: 18 } });
      setResults(data.results || []);
      setSearched(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Search failed — try again");
    } finally { setBusy(false); }
  };

  // Auto-search when opened with a seed query
  useEffect(() => {
    if (open && defaultQ.trim() && !searched) { setQ(defaultQ); runSearch(defaultQ); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md flex items-start justify-center p-4 sm:p-8" onClick={onClose} data-testid="image-search-palette">
      <div
        className="w-full max-w-4xl bg-[#0B0F17] border border-[#22D3EE]/30 rounded-2xl shadow-[0_0_80px_-20px_rgba(34,211,238,0.6)] overflow-hidden text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 pt-4 pb-3 border-b border-white/10 bg-gradient-to-r from-[#22D3EE]/10 to-transparent">
          <div className="flex items-center justify-between mb-2.5">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#22D3EE] font-bold inline-flex items-center gap-1.5">
              <ScanSearch size={11} /> / image search
            </div>
            <button onClick={onClose} className="text-white/60 hover:text-white p-1 rounded hover:bg-white/10" data-testid="image-search-close"><X size={16} /></button>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); runSearch(q); }} className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2.5 bg-white/[0.05] border border-[#22D3EE]/30 rounded-full px-4 py-2.5 focus-within:border-[#22D3EE] focus-within:shadow-[0_0_20px_-6px_rgba(34,211,238,0.8)] transition-all">
              <Search size={15} className="text-[#22D3EE] shrink-0" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search classroom-safe images… e.g. mitosis, volcano, fractions"
                data-testid="image-search-input"
                className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-white/35"
              />
            </div>
            <button
              type="submit"
              disabled={busy || !q.trim()}
              data-testid="image-search-btn"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#22D3EE] to-[#7C3AED] text-white text-xs font-bold hover:brightness-110 disabled:opacity-40 transition"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} Search
            </button>
          </form>
        </div>
        <div className="p-4 max-h-[62vh] overflow-y-auto">
          {busy ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="image-search-loading">
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="aspect-square rounded-lg bg-white/[0.05] border border-white/10 animate-pulse" />
              ))}
            </div>
          ) : results.length === 0 ? (
            <div className="py-16 text-center text-white/40 text-sm" data-testid="image-search-empty">
              <ImageIcon size={28} className="mx-auto mb-3 opacity-40" />
              {searched ? "No images found — try a different keyword." : "Type a keyword and hit Search — CC-licensed, classroom-safe images."}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {results.map((r) => (
                <button
                  key={r.id}
                  onClick={() => { onPick(r); onClose(); }}
                  data-testid={`image-result-${r.id}`}
                  className="group aspect-square overflow-hidden border border-white/10 rounded-lg bg-white/[0.04] hover:border-[#22D3EE] hover:shadow-[0_0_18px_-4px_rgba(34,211,238,0.7)] focus:outline-none relative transition-all"
                  title={r.title}
                >
                  <img src={r.thumbnail} alt={r.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  <span className="absolute inset-x-0 bottom-0 p-1 text-[10px] text-white bg-gradient-to-t from-black/80 to-transparent line-clamp-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {r.creator || r.source}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
